/**
 * Globaler „Rückgängig"-Stack (Undo-Loop Einheit C, Schritt 9; docs/KONZEPT-UNDO.md § 3.3,
 * Zustand docs/LOOP-UNDO.md § 2 Schritt 9). Persönlicher Stack (Antoine, 23.08.2026, siehe
 * docs/LOOP-UNDO.md Kopf): jeder Nutzer sieht/rückgängigt nur seine eigenen letzten Aktionen.
 *
 * Platzierung + Aufbau ausdrücklich am Vorbild `ui/NotificationsBell.tsx` orientiert (Icon-Knopf
 * mit Badge-Zähler in der Topbar, Klick öffnet einen `Drawer`) — genau die vom Auftrag verlangte
 * "Glocke, aber für Rückgängig"-Analogie. `GET /api/undo/stack` liefert bis zu 20 `pending`-
 * Kompensationen DIESES Nutzers, NUR Aktionscodes mit registriertem Handler (Backend-Filter aus
 * Einheit A Schritt 4, `apps/api/src/routes/undo.ts`) — eine Kategorie-C-artige Aktion ohne
 * Handler (z. B. `offer.sent`) taucht hier serverseitig gefiltert nie auf, das ist die
 * Grundlage für Schritt 10, hier nur konsumiert, nicht neu gebaut.
 *
 * Fehlertext bei 409 (Kategorie-C-Kettenabbruch, `precondition_failed`, `not_pending`, ...):
 * `apiMutate`/`toApiError` (`apps/web/src/lib/api.ts`) liest bereits `body.message` in
 * `Error.message` — die serverseitig formulierte Meldung aus `routes/undo.ts` (§ 4.3, wörtlich
 * inkl. blockierendem Aktionscode) wird hier 1:1 angezeigt, nicht durch einen generischen
 * "Fehler ist aufgetreten"-Text ersetzt (harte Vorgabe aus docs/LOOP-UNDO.md).
 *
 * Rückmeldung nach dem Anwenden: kein globaler Toast-Provider im Repo vorhanden (geprüft) — das
 * bestehende Muster für eine kurzlebige Erfolgs-/Fehlermeldung ist eine selbstverwaltete, unten
 * fixierte Leiste mit Auto-Dismiss (Vorbild: der „Undo-Toast" in `pages/builder.tsx` nach dem
 * Entfernen einer Position, Zeile ~2262). Dieselbe Machart hier für Erfolg UND 409-Fehler.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import { Button, Drawer, EmptyState, Icon, Modal } from "./components";
import { timeAgo } from "./format";

interface UndoStackEntry {
  id: string;
  activityLogId: string;
  actionCode: string;
  compensationData: unknown;
  status: "pending" | "applied" | "expired" | "denied";
  appliedAt: string | null;
  createdAt: string;
}

/** Lesbare Beschreibung je Phase-1-Aktionscode (docs/LOOP-UNDO.md § 1 Punkt 2). Ein unbekannter
 *  Code (z. B. eine spätere Phase) faellt auf den rohen Code zurueck statt eine leere Zeile zu
 *  zeigen — besser ein technischer Rest als eine stumme Lücke. */
const ACTION_LABEL: Record<string, string> = {
  "text_snippet.updated": "Textbaustein bearbeitet",
  "offer.title_or_guest_count_changed": "Angebotstitel oder Gästezahl geändert",
  "catalog_item.description_changed": "Katalog-Beschreibungstext geändert",
  "settings.feiertage_enabled_changed": "Einstellung „Feiertage berücksichtigen“ geändert",
  "offer.uebernommen_aus_vorgang": "Variante durch altes Angebot ersetzt",
};

function describeAction(actionCode: string): string {
  return ACTION_LABEL[actionCode] ?? actionCode;
}

export function UndoStack() {
  const [open, setOpen] = useState(false);
  const { data, reload } = useApi<UndoStackEntry[]>("/api/undo/stack");
  const entries = data ?? [];

  const [confirmEntry, setConfirmEntry] = useState<UndoStackEntry | null>(null);
  const [applying, setApplying] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: "success" | "error"; message: string } | null>(null);

  // Beim Öffnen des Drawers neu laden — derselbe Ansatz wie NotificationsBell (der Zähler in der
  // Topbar bleibt vom initialen Ladevorgang gespeist, das Öffnen holt den frischesten Stand).
  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  // Kurzlebige Rückmeldung (Erfolg wie 409-Fehler) verschwindet von selbst — Vorbild: der
  // Undo-Toast in builder.tsx. Manuelles Schließen bleibt zusätzlich möglich (unten, Kreuz-Icon).
  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 6000);
    return () => clearTimeout(t);
  }, [feedback]);

  async function applyUndo(entry: UndoStackEntry) {
    setApplying(true);
    try {
      await apiMutate(`/api/undo/${entry.id}`, "POST");
      setFeedback({ kind: "success", message: `„${describeAction(entry.actionCode)}“ wurde rückgängig gemacht.` });
      setConfirmEntry(null);
      reload();
    } catch (e) {
      // Wörtlich, nicht generisch (harte Vorgabe): apiFetch/toApiError liest bereits
      // body.message aus der 409-Antwort (z. B. den Kategorie-C-Kettenabbruch-Text aus
      // routes/undo.ts, § 4.3) in e.message ein.
      const message = e instanceof Error && e.message ? e.message : "Rückgängig machen ist fehlgeschlagen.";
      setFeedback({ kind: "error", message });
      setConfirmEntry(null);
      // Der Server könnte den Status inzwischen geändert haben (z. B. `not_pending`, wenn ein
      // zweiter Klick/Tab dieselbe Kompensation schon angewendet hat) — Liste neu laden, damit
      // sie nicht auf einem veralteten Stand stehen bleibt.
      reload();
    } finally {
      setApplying(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="relative rounded-full p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-primary dark:text-surface-variant dark:hover:bg-white/10"
        title="Rückgängig"
        aria-label={entries.length > 0 ? `Rückgängig (${entries.length})` : "Rückgängig"}
        onClick={() => setOpen(true)}
      >
        <Icon name="undo" />
        {entries.length > 0 && (
          <span className="absolute right-0.5 top-0.5 grid h-[16px] min-w-[16px] place-items-center rounded-full bg-primary px-1 text-[9.5px] font-bold text-on-primary">
            {entries.length > 9 ? "9+" : entries.length}
          </span>
        )}
      </button>

      {/* Portal an body: die Kopfzeile (z-30) bildet einen eigenen Stapel — darin lag der Drawer
          mobil UNTER der unteren Navigationsleiste (z-40), die seine Fußleiste verdeckte. */}
      {createPortal(
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title="Rückgängig"
        subtitle={entries.length > 0 ? `${entries.length} eigene${entries.length === 1 ? "" : ""} Aktion${entries.length === 1 ? "" : "en"} rückgängig machbar` : undefined}
      >
        {entries.length === 0 ? (
          <EmptyState icon="undo" title="Nichts zum Rückgängigmachen" hint="Deine letzten rückgängig machbaren Aktionen erscheinen hier." />
        ) : (
          <ul className="space-y-1">
            {entries.map((entry) => (
              <li key={entry.id}>
                <div className="flex items-start gap-3 rounded-xl px-3 py-2.5">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <Icon name="undo" className="text-[17px]" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-medium text-on-surface dark:text-inverse-on-surface">
                      {describeAction(entry.actionCode)}
                    </span>
                    <span className="block font-body-sm text-[12px] text-on-surface-variant/80">{timeAgo(entry.createdAt)}</span>
                  </span>
                  <Button
                    variant="secondary"
                    className="shrink-0 !py-1.5 !text-[12px]"
                    onClick={() => setConfirmEntry(entry)}
                  >
                    Rückgängig machen
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Drawer>,
        document.body,
      )}

      {/* Bestätigungsdialog — Undo greift in echte Daten ein, deshalb nie ohne Rückfrage (harte
          Vorgabe). Vorbild fuer den Modal-Aufbau: AngebotSendenDialog (Header/Body/Footer). */}
      <Modal
        open={confirmEntry != null}
        onClose={() => (applying ? undefined : setConfirmEntry(null))}
        width="420px"
        title="Rückgängig machen?"
        subtitle={confirmEntry && describeAction(confirmEntry.actionCode)}
        footer={
          confirmEntry && (
            <>
              <Button variant="secondary" onClick={() => setConfirmEntry(null)} disabled={applying}>
                Abbrechen
              </Button>
              <Button onClick={() => void applyUndo(confirmEntry)} disabled={applying}>
                <Icon name="undo" className="text-[16px]" /> {applying ? "Wird rückgängig gemacht …" : "Ja, rückgängig machen"}
              </Button>
            </>
          )
        }
      >
        <p className="text-[12.5px] leading-relaxed text-on-surface-variant">
          Diese Aktion wird sofort rückgängig gemacht. Wenn sich die betroffenen Daten seitdem geändert haben, wird
          nichts überschrieben — der Vorgang wird dann verweigert.
        </p>
      </Modal>

      {/* Kurzlebige Rückmeldung — Vorbild builder.tsx (Undo-Toast). Fehlertext (auch 409) steht
          hier WÖRTLICH, wie in feedback.message hinterlegt. */}
      {feedback && (
        <div className="fixed inset-x-0 bottom-4 z-[70] flex justify-center px-4 lg:bottom-6">
          <div
            className={`flex max-w-lg items-start gap-3 rounded-xl border px-4 py-2.5 shadow-2xl ${
              feedback.kind === "success"
                ? "border-outline-variant/50 bg-surface-container-lowest dark:border-outline/30 dark:bg-night-card"
                : "border-[#F1D2CB] bg-[#FBEDEA] dark:border-[#B23B32]/30 dark:bg-[#B23B32]/10"
            }`}
          >
            <Icon
              name={feedback.kind === "success" ? "check_circle" : "error"}
              className={`mt-0.5 text-[16px] ${feedback.kind === "success" ? "text-tertiary" : "text-[#B23B32] dark:text-[#e39b92]"}`}
            />
            <span
              className={`text-[12.5px] ${
                feedback.kind === "success" ? "text-on-background dark:text-inverse-on-surface" : "text-[#8F2E26] dark:text-[#e39b92]"
              }`}
            >
              {feedback.message}
            </span>
            <button
              type="button"
              className="ml-auto shrink-0 text-on-surface-variant/70 hover:text-on-surface-variant"
              aria-label="Meldung schließen"
              onClick={() => setFeedback(null)}
            >
              <Icon name="close" className="text-[15px]" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
